// The life of an order, as a log: when it was placed, shipped, delivered,
// canceled or reopened, each return request and the answer to it, each
// return taken in, each refund — the date and time on the left, the event on
// the right, oldest first (the server's reading, aldoo-na/store/order-history).
import React from "react"
import {
  ShoppingBagIcon,
  TruckIcon,
  CheckCircleIcon,
  XCircleIcon,
  ArrowUturnLeftIcon,
  ChatBubbleLeftEllipsisIcon,
  HandThumbUpIcon,
  HandThumbDownIcon,
  NoSymbolIcon,
  ArrowUturnDownIcon,
  ReceiptRefundIcon,
  ArchiveBoxIcon,
  QrCodeIcon,
  ExclamationTriangleIcon,
  BanknotesIcon,
  PencilSquareIcon,
  ArrowsRightLeftIcon,
} from "@heroicons/react/24/outline"
import { describeLine } from "aldoo-ra/Store/lines"
import { money, timeAgo } from "./ui"
import { REASON_LABELS } from "./ReturnDialog"
import { CANCEL_REASON_LABELS } from "./ui"

// what each event is called, its icon and its colour
// the courier's own events (what it says of the parcel), by the state each one tells of
const COURIER_KINDS = {
  delivered: { icon: CheckCircleIcon, tone: "bg-admin_success/15 text-admin_success" },
  ready_for_pickup: { icon: ArchiveBoxIcon, tone: "bg-admin_info/15 text-admin_info" },
  problem: { icon: ExclamationTriangleIcon, tone: "bg-admin_error/15 text-admin_error" },
  returning: { icon: ArrowUturnLeftIcon, tone: "bg-admin_warning/20 text-admin_warning" },
  returned: { icon: ArrowUturnLeftIcon, tone: "bg-admin_warning/20 text-admin_warning" },
}
const COURIER_KIND = { icon: TruckIcon, tone: "bg-black/[0.06] text-black/60" }

const KINDS = {
  waybill_created: { icon: QrCodeIcon, tone: "bg-admin_info/15 text-admin_info" },
  waybill_canceled: { icon: XCircleIcon, tone: "bg-admin_error/15 text-admin_error" },
  placed: { icon: ShoppingBagIcon, tone: "bg-admin_info/15 text-admin_info" },
  shipped: { icon: TruckIcon, tone: "bg-admin_warning/20 text-admin_warning" },
  completed: { icon: CheckCircleIcon, tone: "bg-admin_success/15 text-admin_success" },
  cod_paid: { icon: BanknotesIcon, tone: "bg-admin_success/15 text-admin_success" },
  canceled: { icon: XCircleIcon, tone: "bg-admin_error/15 text-admin_error" },
  reopened: { icon: ArrowUturnLeftIcon, tone: "bg-admin_info/15 text-admin_info" },
  modified: { icon: PencilSquareIcon, tone: "bg-admin_info/15 text-admin_info" },
  moved: { icon: ArrowsRightLeftIcon, tone: "bg-admin_info/15 text-admin_info" },
  request_created: { icon: ChatBubbleLeftEllipsisIcon, tone: "bg-admin_warning/20 text-admin_warning" },
  cancel_requested: { icon: XCircleIcon, tone: "bg-admin_warning/20 text-admin_warning" },
  cancel_withdrawn: { icon: NoSymbolIcon, tone: "bg-admin_neutral_1 text-admin_text_secondary" },
  cancel_accepted: { icon: HandThumbUpIcon, tone: "bg-admin_success/15 text-admin_success" },
  cancel_declined: { icon: HandThumbDownIcon, tone: "bg-admin_error/15 text-admin_error" },
  request_canceled: { icon: NoSymbolIcon, tone: "bg-admin_neutral_1 text-admin_text_secondary" },
  request_accepted: { icon: HandThumbUpIcon, tone: "bg-admin_success/15 text-admin_success" },
  request_declined: { icon: HandThumbDownIcon, tone: "bg-admin_error/15 text-admin_error" },
  returned: { icon: ArrowUturnDownIcon, tone: "bg-[#6b5ca5]/15 text-[#6b5ca5]" },
  all_returned: { icon: ArchiveBoxIcon, tone: "bg-[#6b5ca5]/15 text-[#6b5ca5]" },
  refunded_partial: { icon: ReceiptRefundIcon, tone: "bg-[#b0742a]/15 text-[#b0742a]" },
  refunded_full: { icon: ReceiptRefundIcon, tone: "bg-admin_error/15 text-admin_error" },
  tracking_set: { icon: QrCodeIcon, tone: "bg-admin_info/15 text-admin_info" },
  parcel_returning: { icon: ArrowUturnLeftIcon, tone: "bg-admin_error/15 text-admin_error" },
  parcel_returned: { icon: ArchiveBoxIcon, tone: "bg-admin_error/15 text-admin_error" },
  parcel_problem: { icon: ExclamationTriangleIcon, tone: "bg-admin_error/15 text-admin_error" },
}

/** The event's title, in the screen's words. */
function titleOf(event, t, currency) {
  switch (event.type) {
    // a waybill made with the courier from the order, and one called off
    case "waybill_created":
      return t("Waybill created · %s %s", event.carrier || "", event.detail || "")
    case "waybill_canceled":
      return t("Waybill canceled · %s %s", event.carrier || "", event.detail || "")
    // the courier's words for the parcel, where it was
    case "courier":
      return [event.text, event.place].filter(Boolean).join(" · ") || t`Courier update`
    // an order paid on delivery is only placed: it is paid when the courier collects the money (cod_paid)
    case "placed":
      return event.cashOnDelivery ? t("Order placed · %s", money(event.amount, currency)) : t("Order placed and paid · %s", money(event.amount, currency))
    case "cod_paid":
      return t("Paid on delivery · %s", money(event.amount, currency))
    case "shipped":
      return event.detail ? t("Marked as shipped · tracking %s", event.detail) : t`Marked as shipped`
    case "completed":
      // nobody's hand: the courier said it was delivered, or the seller's days after shipping went by
      if (event.via === "courier") return t`Delivered: confirmed by the courier`
      if (event.via === "auto") return t("Completed automatically, %s days after shipping", event.detail || "")
      return t`Marked as delivered`
    case "tracking_set":
      return t("Tracking number set · %s", event.detail || "")
    case "parcel_returning":
      return t`The courier is returning the parcel to you`
    case "parcel_returned":
      return t`The parcel was delivered back to you`
    case "parcel_problem":
      return t`The courier reports a problem with the parcel`
    case "canceled":
      return t`Order canceled`
    case "reopened":
      return t`Order reopened`
    case "modified":
      return t`Order changed`
    case "moved":
      return t("Moved to another seller · %s", event.detail || "")
    case "cancel_requested":
      return t`Cancellation requested by the buyer`
    case "cancel_withdrawn":
      return t`Cancellation request withdrawn by the buyer`
    case "cancel_accepted":
      return t`Cancellation accepted`
    case "cancel_declined":
      return t`Cancellation declined`
    case "request_created":
      return t("Return request #%s sent by the buyer", event.number)
    case "request_canceled":
      return t("Return request #%s canceled by the buyer", event.number)
    case "request_accepted":
      return t("Return request #%s accepted", event.number)
    case "request_declined":
      return t("Return request #%s declined", event.number)
    case "returned":
      return t("Items returned (%s)", event.units)
    case "all_returned":
      return t`Every item is back`
    case "refunded_partial":
      return t("Partial refund · %s", money(event.amount, currency))
    case "refunded_full":
      return t("Refunded in full · %s", money(event.amount, currency))
    default:
      return event.type
  }
}

const Items = ({ lines }) =>
  lines?.length ? (
    <ul className="m-0 mt-1 p-0 list-none text-xs text-black/70">
      {lines.map((line, index) => (
        <li key={index}>
          {line.quantity} × {describeLine(line, line.name || line.pid)}
          {line.sku ? ` · ${line.sku}` : ""}
        </li>
      ))}
    </ul>
  ) : null

/** `events`: the order's timeline (order.history); `currency`, `locale`, `t`. */
export default function OrderTimeline({ events = [], currency, locale = "en-GB", t }) {
  if (!events.length) return <div className="py-6 text-sm italic text-black/50">{t`Nothing has happened to this order yet`}</div>
  const day = (d) => new Date(d).toLocaleDateString(locale, { dateStyle: "medium" })
  const time = (d) => new Date(d).toLocaleTimeString(locale, { hour: "2-digit", minute: "2-digit" })
  return (
    <ol className="m-0 p-0 list-none py-4" data-testid="order-timeline">
      {events.map((event, index) => {
        const kind = event.type === "courier" ? COURIER_KINDS[event.state] || COURIER_KIND : KINDS[event.type] || KINDS.placed
        const Icon = kind.icon
        const last = index === events.length - 1
        return (
          <li key={index} className="grid grid-cols-[96px_32px_1fr] md:grid-cols-[130px_40px_1fr] gap-x-2 break-inside-avoid" data-testid="order-timeline-event" data-type={event.type}>
            {/* when */}
            <div className="text-right pt-1.5">
              <div className="text-sm font-medium text-black">{day(event.at)}</div>
              <div className="text-xs text-black/50 tabular-nums">{time(event.at)}</div>
              <div className="text-[11px] text-black/40 print:hidden">{timeAgo(event.at, t)}</div>
            </div>
            {/* the rail: a dot per event, joined by a line */}
            <div className="relative flex justify-center">
              {!last && <span className="absolute top-8 bottom-0 w-px bg-black/10" aria-hidden />}
              <span className={`relative z-10 mt-0.5 w-8 h-8 rounded-full flex items-center justify-center ${kind.tone}`}>
                <Icon className="w-4 h-4" aria-hidden />
              </span>
            </div>
            {/* what happened */}
            <div className={`min-w-0 pt-1.5 ${last ? "" : "pb-6"}`}>
              <div className="text-sm font-semibold text-black break-words">{titleOf(event, t, event.currency || currency)}</div>
              {event.byName && <div className="text-xs text-black/50">{t("by %s", event.byName)}</div>}
              {event.type === "courier" && event.carrier && <div className="text-xs text-black/50">{event.carrier}</div>}
              {event.via === "courier" && event.detail && event.type !== "completed" && <div className="text-xs text-black/70 mt-0.5">{event.detail}</div>}
              {event.reason && (
                <div className="text-xs text-black/70 mt-0.5">
                  <span className="text-black/50">{t`Reason`}: </span>
                  {t((event.type?.startsWith("cancel_") ? CANCEL_REASON_LABELS : REASON_LABELS)[event.reason] || event.reason)}
                  {event.late ? ` · ${t`after the return period`}` : ""}
                </div>
              )}
              {event.note && <div className="text-xs text-black/70 mt-0.5 whitespace-pre-line">“{event.note}”</div>}
              {event.message && <div className="mt-1 rounded-md bg-black/[0.04] px-2.5 py-1.5 text-xs text-black/80 whitespace-pre-line">{event.message}</div>}
              {/* what the seller wrote to the buyer when calling the order off */}
              {event.type === "canceled" && event.detail && <div className="mt-1 rounded-md bg-black/[0.04] px-2.5 py-1.5 text-xs text-black/80 whitespace-pre-line">{event.detail}</div>}
              {/* the seller changed the order: each line's units before → after, the total before → after */}
              {event.type === "modified" && (
                <div className="mt-1 flex flex-col gap-0.5 text-xs text-black/70" data-testid="timeline-changes">
                  {(event.changes || []).map((change, index) => (
                    <div key={index}>
                      {change.name}: {change.to === 0 ? t`taken out` : change.from === 0 ? t("added, %s pcs", change.to) : `${change.from} → ${change.to}`}
                    </div>
                  ))}
                  {Number.isFinite(event.amountBefore) && Number.isFinite(event.amountAfter) && (
                    <div className="text-black/50">
                      {t`Total`}: {money(event.amountBefore, event.currency || currency)} → {money(event.amountAfter, event.currency || currency)}
                    </div>
                  )}
                </div>
              )}
              <Items lines={event.lines} />
              {event.shipping > 0 && <div className="text-xs text-black/70">{t("Delivery · %s", money(event.shipping, event.currency || currency))}</div>}
              {event.type === "returned" && <div className="text-xs text-black/50 mt-0.5">{event.restock ? t`Back on sale` : t`Kept out of stock`}</div>}
              {(event.type === "refunded_partial" || event.type === "refunded_full") && event.total > 0 && (
                <div className="text-xs text-black/50 mt-0.5">{t("Refunded so far: %s", money(event.total, event.currency || currency))}</div>
              )}
            </div>
          </li>
        )
      })}
    </ol>
  )
}
